import { useEffect, useMemo, useState } from "react"
import { Autocomplete, MenuItem, TextField } from "@mui/material"
import { OpenSchedule } from "event-definitions"
import { parentTreeItem } from "library/tree/index.mjs"
import { useDebouncedValue } from "lib/@hooks/useDebouncedValue"
import { useTreeItemIndex } from "routes/schedule/lib/useTreeItemIndex"
import { SCHEDULE } from "library/constants.mjs"
import { isIdInGroup } from "library/id-helpers.mjs"
import Iconify from "minimals-template/components/Iconify"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { Frag } from "lib/@components/slot/frag"
import { getScheduleSearchScore, SCHEDULE_SEARCH_RESULT_THRESHOLD } from "./search-schedule-score"

function checkForUnlicensed(c) {
    if (!c.children) return c.notLicensed
    return c.children.some((c) => checkForUnlicensed(c))
}

export function SearchSchedules() {
    const [currentOptions, setCurrentOptions] = useState([])
    const [value, setValue, current] = useDebouncedValue("", 400)
    const [open, setOpen] = useState(false)
    const treeIndex = useTreeItemIndex(false)

    const searchable = useMemo(
        () =>
            Object.values(treeIndex)
                .filter(
                    (c) =>
                        !!c.$ &&
                        !!c.label &&
                        typeof c.label === "string" &&
                        !c.isAlias &&
                        !c.folderHandling &&
                        c[parentTreeItem].label !== "Retired" &&
                        c.type !== "group"
                )
                .unique((c) => `${c.code}-${c.label}`),
        [treeIndex]
    )

    useEffect(() => {
        if (!current.trim()) {
            setCurrentOptions([])
            setOpen(false)
            return
        }
        if (isIdInGroup(current)) {
            setOpen(false)
            setCurrentOptions([])
            return
        }
        const list = [
            ...searchable.map((c) => ({
                label: c.label,
                code: c.code,
                type: c.hasChildren ? "folder" : "schedule",
                notLicensed: checkForUnlicensed(c),
                value: c.id,
                item: c,
                d: getScheduleSearchScore(current, c),
            })),
        ].sortBy("d", true)
        setCurrentOptions(list.filter((f) => f.d > SCHEDULE_SEARCH_RESULT_THRESHOLD))

        setOpen(true)
    }, [current, searchable])
    return (
        <Autocomplete
            sx={{ my: 2 }}
            blurOnSelect
            clearOnBlur
            value={null}
            open={open && !!value.length}
            componentsProps={{ popper: { sx: { zIndex: 6000 } } }}
            filterOptions={(o) => o}
            isOptionEqualToValue={() => false}
            onChange={handleAutocompleteChange}
            onClose={handleClose}
            inputValue={isIdInGroup(value) ? "" : value}
            onInputChange={handleInputChange}
            popupIcon={<Frag />}
            data-cy="search-schedules-autocomplete"
            renderOption={(props, option) => (
                <MenuItem
                    {...props}
                    key={option.value}
                    sx={{ color: option.notLicensed ? "unlicensed.dark" : undefined }}
                    data-cy="search-schedules-results-item"
                >
                    {option.type === "folder" ? (
                        <Iconify
                            sx={{
                                display: "inline-block",
                                mr: 1,
                                color: option.notLicensed ? "unlicensed.main" : "primary.main",
                            }}
                            icon="material-symbols:folder-rounded"
                        />
                    ) : (
                        <Iconify
                            sx={{
                                display: "inline-block",
                                mr: 1,
                                color: option.notLicensed ? "unlicensed.main" : "primary.main",
                            }}
                            icon="mdi:file-document-outline"
                        />
                    )}{" "}
                    {option.code ? `${option.code} - ` : ""} {option.label}
                </MenuItem>
            )}
            renderInput={(props) => (
                <TextField
                    autoFocus
                    className="search-field"
                    placeholder="Search..."
                    variant="standard"
                    {...props}
                    inputProps={{
                        ...props.inputProps,
                        className: "search-input",
                    }}
                />
            )}
            options={currentOptions}
        />
    )

    function handleClose() {
        setOpen(false)
    }

    function handleInputChange(_, nextValue, reason) {
        if (reason === "reset") return
        setValue(nextValue)
    }

    function handleAutocompleteChange(_, option) {
        if (!option) return
        if (option.notLicensed && option.type !== "folder") {
            errorSnackbar("You have not licensed that schedule!")
            return
        }
        select(option).catch(console.error)
    }

    async function select(option) {
        setValue("")
        setOpen(false)
        setCurrentOptions([])
        if (option?.item?.$?.type === SCHEDULE) {
            await OpenSchedule(option.item[parentTreeItem].id).raiseAsync({
                ...option.item[parentTreeItem],
                type: "folder",
            })
        }
        if (option?.value && option?.item) {
            await OpenSchedule(option.value).raiseAsync(option.item)
            setTimeout(() => focusChosenCheckbox(option.value), 100)
        }
    }
}

function focusChosenCheckbox(scheduleId) {
    const row = document.querySelector(`[chosenscheduleid="${CSS.escape(scheduleId)}"]`)
    row?.querySelector('[data-cy="selected-checkbox"] input[type="checkbox"]')?.focus()
}
